import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { clsx } from 'clsx';

import { Select, Skeleton, Button, TOption } from '@treezor/design-system';

import { useAppSelector, useAppDispatch } from '@/hooks/redux-helpers';

import { ITemplate, ITemplateModel } from '@/store/models/template.model';

import { openModal } from '@/store/actions/core.actions';

import { searchTemplates } from '@/store/actions/template.actions';
import { TemplatesError } from '@/pages/admin/template/TemplatesError';

import { ITemplateVariablesModalProps } from './TemplateVariablesModal';

interface ITemplateFilter {
  templates?: ITemplate[];
  currentModel?: ITemplateModel;
  onTemplateChange: (value: string) => void;
}

export const TemplateFilter = ({ templates, currentModel, onTemplateChange }: ITemplateFilter) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const isLoading = useAppSelector((state) => state.template.templateGroups.isLoading);

  const templateGroupsError = useAppSelector((state) => state.template.templateGroups.error);

  const templatesOptions: TOption[] =
    templates?.map((template) => ({ label: template.name, value: template.fullName })) || [];

  const handleOpenVariablesModal = useCallback(() => {
    dispatch(
      openModal<ITemplateVariablesModalProps>('templatesVariablesModal', { variables: currentModel?.variables }),
    );
  }, [currentModel?.variables, dispatch]);

  const handleDispatchSearchTemplates = useCallback(() => dispatch(searchTemplates()), [dispatch]);

  if (isLoading) return <Skeleton width="27%" height={40} margin="1rem 1rem" />;

  if (templateGroupsError)
    return (
      <TemplatesError
        message={t('pages.administration.templates.templatesLoadingError')}
        onReload={handleDispatchSearchTemplates}
      />
    );

  if (!templates?.length)
    return (
      <span className="typo-body-regular-paragraphLarge text-helper flex justify-center items-center">
        {t('pages.administration.templates.noTemplates')}
      </span>
    );

  return (
    <div className="flex gap-4 items-center py-4 px-5 min-h-[5rem]">
      <div className={clsx(!!currentModel ? 'relative' : 'absolute', 'w-[20rem] z-[2]')}>
        <Select
          placeholder={t('pages.administration.templates.select')}
          options={templatesOptions}
          onChange={onTemplateChange}
          defaultValue={currentModel?.name}
        />
      </div>
      {currentModel?.variables && (
        <Button variant="ghost" className="uppercase mx-4" size="s" onClick={handleOpenVariablesModal}>
          {t('pages.administration.templates.variables.action')}
        </Button>
      )}
    </div>
  );
};
